import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { PORTFOLIO_CATEGORIES, PORTFOLIO_ITEMS } from "@/data/site";

const CARD_THEMES = [
  "from-electric/80 via-navy-950 to-navy-950",
  "from-cyan-500/70 via-navy-950 to-navy-950",
  "from-purple-500/70 via-navy-950 to-navy-950",
  "from-electric-bright/70 via-navy to-navy-950",
  "from-indigo-500/70 via-navy-950 to-navy-950",
  "from-sky-500/70 via-navy to-navy-950",
];

const Portfolio = () => {
  const [active, setActive] = useState("All");
  const items =
    active === "All" ? PORTFOLIO_ITEMS : PORTFOLIO_ITEMS.filter((p) => p.category === active);

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="scroll-mt-24 bg-slate-50 bg-grid-light py-24 md:py-32 lg:py-40"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <SectionHeading
          overline="Portfolio"
          title="Selected Solutions"
          text="A selection of solution types we design and build. Full case studies are being prepared and will be published here soon."
        />

        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap gap-2.5" role="tablist" aria-label="Portfolio categories">
            {PORTFOLIO_CATEGORIES.map((category) => (
              <button
                key={category}
                role="tab"
                aria-selected={active === category}
                data-testid={`portfolio-filter-${category.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setActive(category)}
                className={`rounded-full px-5 py-2.5 text-sm font-medium transition-[background-color,color,border-color] duration-300 ${
                  active === category
                    ? "bg-navy-950 text-white"
                    : "border border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {items.map((item, i) => (
              <motion.article
                layout
                key={item.category}
                data-testid={`portfolio-item-${item.category.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-navy-950 shadow-sm"
              >
                <div
                  className={`relative h-52 bg-gradient-to-br ${CARD_THEMES[i % CARD_THEMES.length]} bg-grid-dark transition-transform duration-700 group-hover:scale-[1.03]`}
                >
                  <span className="glass-chip absolute left-5 top-5 rounded-full px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-200">
                    {item.status}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-4 p-6">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-electric-bright">
                      {item.category}
                    </p>
                    <h3 className="mt-1.5 font-display text-lg font-medium text-white">
                      {item.title}
                    </h3>
                  </div>
                  <ArrowUpRight className="h-5 w-5 shrink-0 text-slate-500 transition-[color,transform] duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-cyan-400" />
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default Portfolio;
